// Forensic trace viewer for saved decks. Reads the AgentEvent stream that
// the build-deck SSE endpoint persisted alongside the deck, and renders it
// as a turn-by-turn timeline: classifier decision → turn N (tool_call …
// tool_result …) → final_answer.
//
// Tool inputs render as collapsible JSON blocks so the big ones (e.g. a
// search_cards filter with a dozen clauses) don't blow up the page, but are
// one click away when you're trying to answer "why did it query mono-red?"

import { useState } from "react";
import {
  Bot,
  ChevronRight,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Flag,
  Wrench,
} from "lucide-react";
import type { AgentEvent } from "../lib/types";
import type { DeckBuildSettings } from "../lib/api";
import { cn } from "../lib/cn";

interface Props {
  events: AgentEvent[];
  /** Optional: build-time toggles persisted on `deck_builds` (exemplar
   *  mode, progressive discovery). The classifier output and image
   *  context already come through via `events`, so those don't need to
   *  be duplicated here. Pass undefined for legacy decks with no build
   *  row — the corresponding chips are simply skipped. */
  settings?: DeckBuildSettings;
}

interface Turn {
  number: number;
  start?: Extract<AgentEvent, { type: "turn_start" }>;
  end?: Extract<AgentEvent, { type: "turn_end" }>;
  assistantText: Array<Extract<AgentEvent, { type: "assistant_text" }>>;
  calls: Array<{
    call: Extract<AgentEvent, { type: "tool_call" }>;
    result?: Extract<AgentEvent, { type: "tool_result" }>;
  }>;
}

function groupTurns(events: AgentEvent[]): Turn[] {
  const byNum = new Map<number, Turn>();
  const getTurn = (n: number): Turn => {
    let t = byNum.get(n);
    if (!t) {
      t = { number: n, assistantText: [], calls: [] };
      byNum.set(n, t);
    }
    return t;
  };
  for (const e of events) {
    if (e.type === "turn_start") getTurn(e.turn).start = e;
    else if (e.type === "turn_end") getTurn(e.turn).end = e;
    else if (e.type === "assistant_text") getTurn(e.turn).assistantText.push(e);
    else if (e.type === "tool_call")
      getTurn(e.turn).calls.push({ call: e });
    else if (e.type === "tool_result") {
      // Match by tool_use_id within the same turn.
      const t = getTurn(e.turn);
      const slot = t.calls.find((c) => c.call.tool_use_id === e.tool_use_id);
      if (slot) slot.result = e;
      else t.calls.push({ call: { type: "tool_call", turn: e.turn, name: "(orphan)", input: {}, tool_use_id: e.tool_use_id }, result: e });
    }
  }
  return [...byNum.values()].sort((a, b) => a.number - b.number);
}

export default function TracePanel({ events, settings }: Props) {
  if (events.length === 0) {
    return (
      <div className="text-xs text-fg-muted">
        No trace saved for this deck (likely built before trace persistence,
        or via the CLI).
      </div>
    );
  }

  const classifier = events.find((e) => e.type === "classifier") as
    | Extract<AgentEvent, { type: "classifier" }>
    | undefined;
  const classifierErr = events.find((e) => e.type === "classifier_error") as
    | Extract<AgentEvent, { type: "classifier_error" }>
    | undefined;
  const imageContext = events.find((e) => e.type === "image_context") as
    | Extract<AgentEvent, { type: "image_context" }>
    | undefined;
  const finalAnswer = events.find((e) => e.type === "final_answer") as
    | Extract<AgentEvent, { type: "final_answer" }>
    | undefined;
  const error = events.find((e) => e.type === "error") as
    | Extract<AgentEvent, { type: "error" }>
    | undefined;
  const turns = groupTurns(events);
  const toolCallCount = turns.reduce((s, t) => s + t.calls.length, 0);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-2 text-[11px] text-fg-subtle font-mono tabular-nums">
        <Bot size={13} strokeWidth={1.75} className="text-fg-muted" />
        <span>{turns.length} {turns.length === 1 ? "pass" : "passes"}</span>
        <span>·</span>
        <span>{toolCallCount} tool calls</span>
        {classifier && (
          <>
            <span>·</span>
            <span>
              classifier: <span className="text-accent">{classifier.category}</span>
              {classifier.exemplars_gated
                ? " (exemplars gated)"
                : " (exemplars enabled)"}
            </span>
          </>
        )}
        {classifierErr && (
          <>
            <span>·</span>
            <span className="text-warn">classifier failed</span>
          </>
        )}
        {/* Build settings the classifier doesn't already cover. Only
            render when explicitly provided; legacy decks without a
            build row pass undefined. */}
        {settings?.exemplars_enabled !== undefined && !classifier && (
          <>
            <span>·</span>
            <span>
              exemplars:{" "}
              <span className={settings.exemplars_enabled ? "text-accent" : "text-warn"}>
                {settings.exemplars_enabled ? "enabled" : "disabled"}
              </span>
            </span>
          </>
        )}
        {settings?.progressive_discovery !== undefined && (
          <>
            <span>·</span>
            <span>
              questions:{" "}
              <span className={settings.progressive_discovery ? "text-accent" : "text-fg-muted"}>
                {settings.progressive_discovery ? "enabled" : "disabled"}
              </span>
            </span>
          </>
        )}
        {imageContext && imageContext.image_count > 0 && (
          <>
            <span>·</span>
            <span>
              images: <span className="text-accent">{imageContext.image_count}</span>
            </span>
          </>
        )}
      </header>

      {imageContext && imageContext.image_count > 0 && imageContext.extracted_text && (
        <details className="rounded-md border border-border bg-bg-raised/30 p-3 group">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-fg-subtle list-none flex items-center gap-1">
            <ChevronRight
              size={11}
              strokeWidth={2}
              className="transition-transform group-open:rotate-90"
            />
            <span>
              what the agent read from {imageContext.image_count} attached image
              {imageContext.image_count === 1 ? "" : "s"}
            </span>
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words text-xs leading-relaxed text-fg-muted font-sans">
            {imageContext.extracted_text}
          </pre>
        </details>
      )}

      <ol className="space-y-3">
        {turns.map((t) => (
          <TurnRow key={t.number} turn={t} />
        ))}
      </ol>

      {finalAnswer && (
        <div className="flex items-start gap-2 text-xs text-success pt-2 border-t border-border">
          <Flag size={12} strokeWidth={2} className="mt-0.5 shrink-0" />
          <span>final answer emitted ({finalAnswer.answer.length} chars)</span>
        </div>
      )}
      {error && (
        <div className="flex items-start gap-2 text-xs text-danger pt-2 border-t border-border">
          <CircleAlert size={12} strokeWidth={2} className="mt-0.5 shrink-0" />
          <span>error: {error.error}</span>
        </div>
      )}
    </div>
  );
}

function TurnRow({ turn }: { turn: Turn }) {
  const combinedText = turn.assistantText.map((a) => a.text).join("\n\n").trim();
  return (
    <li className="rounded-md border border-border bg-bg-raised/30 p-3 space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="font-mono text-xs text-fg-muted tabular-nums">
          pass {turn.number}
        </div>
        <div className="font-mono text-[11px] text-fg-subtle tabular-nums">
          {turn.end?.stop_reason && (
            <>stop={turn.end.stop_reason}</>
          )}
          {turn.end?.tokens != null && (
            <> · {turn.end.tokens}t</>
          )}
        </div>
      </div>

      {combinedText && (
        <details className="group">
          <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-fg-subtle list-none flex items-center gap-1">
            <ChevronRight
              size={11}
              strokeWidth={2}
              className="transition-transform group-open:rotate-90"
            />
            <span>reasoning ({combinedText.length} chars)</span>
          </summary>
          <pre className="mt-2 whitespace-pre-wrap break-words text-xs leading-relaxed text-fg-muted font-sans">
            {combinedText}
          </pre>
        </details>
      )}

      {turn.calls.length > 0 && (
        <ul className="space-y-1.5">
          {turn.calls.map((c, i) => (
            <ToolCallRow key={c.call.tool_use_id || i} call={c.call} result={c.result} />
          ))}
        </ul>
      )}
    </li>
  );
}

function ToolCallRow({
  call,
  result,
}: {
  call: Extract<AgentEvent, { type: "tool_call" }>;
  result?: Extract<AgentEvent, { type: "tool_result" }>;
}) {
  const [open, setOpen] = useState(false);
  const inputJson = (() => {
    try {
      return JSON.stringify(call.input, null, 2);
    } catch {
      return "(unserializable input)";
    }
  })();
  const inputPreview = (() => {
    try {
      const s = JSON.stringify(call.input);
      return s.length > 100 ? `${s.slice(0, 100)}…` : s;
    } catch {
      return "";
    }
  })();

  return (
    <li className="text-xs font-mono">
      <button
        type="button"
        className="flex items-start gap-1.5 w-full text-left hover:bg-bg-raised/60 rounded px-1.5 py-1 -mx-1.5"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <ChevronDown size={12} strokeWidth={2} className="mt-0.5 text-fg-subtle shrink-0" />
        ) : (
          <ChevronRight size={12} strokeWidth={2} className="mt-0.5 text-fg-subtle shrink-0" />
        )}
        <Wrench size={12} strokeWidth={2} className="mt-0.5 text-accent shrink-0" />
        <span className="text-accent font-medium">{call.name}</span>
        <span className="text-fg-subtle truncate">{inputPreview}</span>
        {result && (
          <span className="ml-auto flex items-center gap-1 shrink-0">
            {result.ok ? (
              <CircleCheck size={11} strokeWidth={2} className="text-success" />
            ) : (
              <CircleAlert size={11} strokeWidth={2} className="text-danger" />
            )}
            <span className={cn(
              "text-[10px] uppercase tracking-wider",
              result.ok ? "text-success" : "text-danger",
            )}>
              {result.ok ? "ok" : "error"}
            </span>
          </span>
        )}
      </button>
      {open && (
        <div className="mt-1 ml-6 space-y-1.5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-fg-subtle mb-1">input</div>
            <pre className="bg-bg border border-border rounded p-2 overflow-x-auto text-[11px] leading-relaxed">
              {inputJson}
            </pre>
          </div>
          {result?.preview && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-fg-subtle mb-1">
                result preview
              </div>
              <pre className="bg-bg border border-border rounded p-2 overflow-x-auto text-[11px] leading-relaxed text-fg-muted">
                {result.preview}
              </pre>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
